import type { FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useForm, useFormState } from "react-hook-form";
import { Info } from "lucide-react";
import { useCreateObservationCategory } from "src/features/Admin/ObservationsCategories/hooks/useCreateObservationCategory";
import { useUpdateObservationCategory } from "src/features/Admin/ObservationsCategories/hooks/useUpdateObservationCategory";
import { z } from "zod";
import { Button } from "src/components/ui/button";

const CreateNewObservationsCategorySchema = z.object({
	name: z.string().min(1, "Name is required")
});

type FormSchema = z.infer<typeof CreateNewObservationsCategorySchema>;

export type CreateNewObservationsCategoryFormProps = {
	defaultValues?: FormSchema;

	categoryId?: number;

	isLoading?: boolean;

	onClose: () => void;
};

const CreateNewObservationsCategoryForm = ({
	defaultValues,
	categoryId,
	isLoading,
	onClose
}: CreateNewObservationsCategoryFormProps) => {
	const [serverError, setServerError] = useState<string | null>(null);

	const resolver = zodResolver(CreateNewObservationsCategorySchema);

	const { register, handleSubmit, control, reset } = useForm<FormSchema>({
		resolver,
		defaultValues
	});

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	const { isValid, errors } = useFormState({ control });

	const {
		mutateAsync: createObservationCategory,
		isLoading: isCreatingObservationCategory
	} = useCreateObservationCategory();

	const {
		mutateAsync: updateObservationCategory,
		isLoading: isUpdatingObservationCategory
	} = useUpdateObservationCategory();

	const onValid = async (data: FormSchema) => {
		const { name } = data;

		try {
			if (categoryId) {
				await updateObservationCategory({ name, categoryId });
			} else {
				await createObservationCategory({ name });
			}

			onClose();
		} catch (error) {
			console.error("Error creating observation category", error);

			if (error instanceof AxiosError) {
				const message = error.response?.data.name[0];
				setServerError(message);
			}
		}
	};

	const onInvalid = (errors: FieldErrors<FormSchema>) => {
		console.warn("Validation error", errors);
	};

	const hasError = Boolean(serverError) || Boolean(errors.name);

	return (
		<div>
			<form onSubmit={handleSubmit(onValid, onInvalid)}>
				<div className={hasError ? "text-red-600" : ""}>
					<div className="flex flex-row items-end justify-between mb-1">
						<label className="text-base">Category title</label>
						<span className="text-xs font-normal">Required</span>
					</div>

					<div className="relative">
						<input
							type="text"
							className={`w-full h-10 px-3 bg-white rounded border ${hasError ? "border-red-600" : "border-border"} disabled:opacity-50`}
							placeholder="Please enter category title"
							disabled={isLoading}
							{...register("name")}
						/>

						{isLoading && (
							<span className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
						)}
					</div>

					{serverError && (
						<p className="text-destructive mt-1 text-xs flex items-center gap-1">
							<Info size={16} />
							{serverError}
						</p>
					)}
				</div>

				<div className="flex justify-between mt-10">
					<Button
						variant="outline"
						type="button"
						onClick={onClose}
						className="min-w-[160px]"
					>
						Cancel
					</Button>

					<Button
						variant="default"
						type="submit"
						className="min-w-[160px]"
						disabled={
							!isValid ||
							isCreatingObservationCategory ||
							isUpdatingObservationCategory
						}
					>
						{isCreatingObservationCategory || isUpdatingObservationCategory
							? "Loading..."
							: categoryId
								? "Update"
								: "Create"}
					</Button>
				</div>
			</form>
		</div>
	);
};

export default CreateNewObservationsCategoryForm;
